import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Box, CircularProgress } from '@mui/material';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authDownload, authFetch } from '../utils/authFetch';
import { sanitizeFileNameSegment } from '../utils/stringSanitization';
import { calculatePixelsPerFrame } from '../components/VideoSettings';
import { formatFramesToClock } from '../utils/cardTiming';
import './Renders.css';
import { SkeletonTable } from '../components/Skeleton';

interface RenderItem {
  version: number;
  status: string;
  created_at: string;
  file_name: string;
  video_settings: {
    background_color?: string;
    fps_num?: number;
    resolution?: string;
    transparent?: boolean;
    video_time?: number;
    video_time_frames?: number;
    aspect_ratio?: string;
    video_format?: string;
  };
}

const RENDERS_REFRESH_INTERVAL_MS = 10000;

const listRenders = async (projectId: string): Promise<RenderItem[]> => {
  const response = await authFetch(`${getApiUrl()}/list-renders?projectId=${projectId}`);
  if (!response.ok) throw new Error('Failed to fetch renders');
  const data = await response.json();
  return data.renders || [];
};

const getRender = async (
  projectId: string,
  version: number,
  fileName: string,
  projectName: string,
  videoFormat?: string,
  resolution?: string,
  aspectRatio?: string
): Promise<void> => {
  const extension = fileName.includes('.') ? fileName.split('.').pop() || 'mp4' : 'mp4';
  const downloadFileName = [
    sanitizeFileNameSegment(projectName, { fallback: 'project', lowercase: false }),
    sanitizeFileNameSegment(videoFormat, { fallback: extension, lowercase: false }),
    sanitizeFileNameSegment(resolution, { fallback: 'res', lowercase: false }),
    sanitizeFileNameSegment(aspectRatio, { fallback: 'aspect' }),
    `v${version}`,
    new Date().toISOString().split('T')[0],
  ].join('_') + `.${extension}`;
  const downloadUrl = `${getApiUrl()}/get-render?projectId=${encodeURIComponent(
    projectId
  )}&version=${encodeURIComponent(String(version))}&downloadName=${encodeURIComponent(downloadFileName)}`;

  await authDownload(downloadUrl, downloadFileName);
};

const deleteRender = async (projectId: string, version: number): Promise<void> => {
  const response = await authFetch(`${getApiUrl()}/delete-render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, version }),
  });
  if (!response.ok) throw new Error('Failed to delete render');
};

const formatDateTime = (dateString: string): string => {
  const utcDate = new Date(dateString + 'Z');
  const localDate = toZonedTime(utcDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
  return format(localDate, 'dd/MM/yyyy - HH:mm:ss');
};

const getResolutionLabel = (videoSettings?: RenderItem['video_settings']): string => {
  if (!videoSettings) return '';
  if (videoSettings.resolution?.trim()) return videoSettings.resolution;

  const rawWidth = (videoSettings as Record<string, unknown>).video_width ?? (videoSettings as Record<string, unknown>).width;
  const width = typeof rawWidth === 'number' ? rawWidth : Number(rawWidth);
  if (!Number.isFinite(width)) return '';
  if (width >= 4000) return '4K';
  if (width >= 3800) return 'UHD';
  if (width >= 2000) return '2K';
  if (width >= 1200) return 'HD';
  return `${Math.round(width)}px`;
};

const getDurationFrames = (params: any): number | null => {
  const vs = params.data?.video_settings;
  const durationFrames = Number(vs?.video_time_frames);
  if (Number.isFinite(durationFrames)) return durationFrames;

  const videoTime = Number(vs?.video_time);
  const framesPerSecond = Number(vs?.fps_num);
  return Number.isFinite(videoTime) && Number.isFinite(framesPerSecond)
    ? Math.round(videoTime * framesPerSecond)
    : null;
};

const formatDuration = (params: any): string => {
  const durationFrames = Number(params.value);
  const framesPerSecond = Number(params.data?.video_settings?.fps_num);
  if (!Number.isFinite(durationFrames) || !Number.isFinite(framesPerSecond) || durationFrames < 0 || framesPerSecond <= 0) {
    return '';
  }
  return formatFramesToClock(Math.round(durationFrames), framesPerSecond);
};

const StatusCellRenderer = (props: { value: string }) => {
  const statusNum = parseInt(props.value, 10);
  if (statusNum === -1) return <span style={{ color: 'red', fontWeight: 'bold' }}>{t`Failed`}</span>;
  if (statusNum === 100) return <span style={{ color: 'green', fontWeight: 'bold' }}>{t`Complete`}</span>;
  if (statusNum === 0) return <span style={{ color: 'orange', fontWeight: 'bold' }}>{t`Pending`}</span>;
  return <span style={{ color: 'orange', fontWeight: 'bold' }}>{statusNum}%</span>;
};

const RefreshCountdown = memo(({ nextRefreshAt, refreshing }: { nextRefreshAt: number; refreshing: boolean }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, []);

  const remainingMs = Math.max(0, nextRefreshAt - now);
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const elapsedPercent = Math.min(100, Math.max(0, ((RENDERS_REFRESH_INTERVAL_MS - remainingMs) / RENDERS_REFRESH_INTERVAL_MS) * 100));

  return (
    <Box
      className="renders-refresh-ring"
      aria-label={refreshing ? String(t`Updating renders`) : String(t`Next renders update in ${remainingSeconds} seconds`)}
    >
      <CircularProgress variant="determinate" value={refreshing ? 100 : elapsedPercent} size={38} thickness={4} />
      <span>{refreshing ? '...' : `${remainingSeconds}s`}</span>
    </Box>
  );
});

const Renders: React.FC = () => {
  const { selectedProject } = useProject();
  const canEdit = selectedProject?.permission_tier === 1 || selectedProject?.permission_tier === 2;
  useLingui();
  const [renders, setRenders] = useState<RenderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextRefreshAt, setNextRefreshAt] = useState(() => Date.now() + RENDERS_REFRESH_INTERVAL_MS);
  const [downloadingVersions, setDownloadingVersions] = useState<Set<number>>(() => new Set());
  const downloadingVersionsRef = useRef<Set<number>>(new Set());

  const fetchRenders = useCallback(async () => {
    if (!selectedProject) {
      setError(String(t`No project selected`));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setRenders(await listRenders(selectedProject.id));
    } catch (err) {
      console.error('Error fetching renders:', err);
      setError(String(t`Failed to load renders`));
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    const refreshRenders = () => {
      setNextRefreshAt(Date.now() + RENDERS_REFRESH_INTERVAL_MS);
      fetchRenders();
    };

    refreshRenders();
    const interval = setInterval(refreshRenders, RENDERS_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchRenders]);

  const handleDownload = useCallback(async (renderItem: RenderItem) => {
    if (!selectedProject) return;
    const { version, file_name: fileName, video_settings: videoSettings } = renderItem;

    if (downloadingVersionsRef.current.has(version)) return;
    downloadingVersionsRef.current.add(version);
    setDownloadingVersions((previous) => new Set(previous).add(version));

    try {
      await getRender(
        selectedProject.id,
        version,
        fileName,
        selectedProject.name,
        videoSettings?.video_format,
        getResolutionLabel(videoSettings),
        videoSettings?.aspect_ratio
      );
    } catch (err) {
      console.error('Error downloading render:', err);
      alert(String(t`Failed to download render`));
    } finally {
      downloadingVersionsRef.current.delete(version);
      setDownloadingVersions((previous) => {
        const next = new Set(previous);
        next.delete(version);
        return next;
      });
    }
  }, [selectedProject]);

  const handleDelete = useCallback(async (version: number) => {
    if (!selectedProject || !window.confirm(String(t`Are you sure you want to delete render version ${version}?`))) return;

    try {
      await deleteRender(selectedProject.id, version);
      await fetchRenders();
    } catch (err) {
      console.error('Error deleting render:', err);
      alert(String(t`Failed to delete render`));
    }
  }, [fetchRenders, selectedProject]);

  const ActionsCellRenderer = useCallback((props: { data: RenderItem }) => {
    const renderItem = props.data;
    const isComplete = parseInt(renderItem.status, 10) === 100;
    const isDownloading = downloadingVersions.has(renderItem.version);
    const canDownload = isComplete && !isDownloading;

    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => handleDownload(renderItem)}
          disabled={!canDownload}
          style={{
            padding: '4px 12px',
            cursor: canDownload ? 'pointer' : 'not-allowed',
            opacity: canDownload ? 1 : 0.5,
            backgroundColor: canDownload ? '#319204' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
          }}
        >
          {isDownloading ? t`Downloading...` : t`Download`}
        </button>
        {canEdit && (
          <button
            onClick={() => handleDelete(renderItem.version)}
            style={{
              padding: '4px 12px',
              cursor: 'pointer',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
            }}
          >
            {t`Delete`}
          </button>
        )}
      </div>
    );
  }, [canEdit, downloadingVersions, handleDelete, handleDownload]);

  const columnDefs = useMemo<ColDef[]>(() => [
    { field: 'version', headerName: String(t`Version`), filter: true, width: 80 },
    { field: 'status', headerName: String(t`Status`), filter: true, cellRenderer: StatusCellRenderer, width: 110, maxWidth: 130 },
    {
      field: 'video_settings.video_format',
      headerName: String(t`Video Format`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.video_format,
      width: 130,
      maxWidth: 150,
    },
    {
      field: 'video_settings.fps_num',
      headerName: String(t`FPS`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.fps_num,
      width: 80,
      maxWidth: 95,
    },
    {
      field: 'video_settings.resolution',
      headerName: String(t`Resolution`),
      filter: true,
      valueGetter: (params: any) => getResolutionLabel(params.data?.video_settings),
      width: 115,
      maxWidth: 135,
    },
    {
      field: 'video_settings.video_time_frames',
      headerName: String(t`Duration`),
      filter: true,
      valueGetter: getDurationFrames,
      valueFormatter: formatDuration,
      width: 140,
      maxWidth: 160,
    },
    {
      headerName: String(t`Px/Frame`),
      filter: true,
      valueGetter: (params: any) => {
        const vs = params.data?.video_settings;
        if (!vs?.resolution || !vs?.aspect_ratio) return null;
        return calculatePixelsPerFrame({
          resolution: vs.resolution,
          aspect_ratio: vs.aspect_ratio,
          fps_num: vs.fps_num,
          video_time: vs.video_time,
          video_time_frames: vs.video_time_frames,
        });
      },
      width: 100,
      maxWidth: 115,
    },
    {
      field: 'video_settings.aspect_ratio',
      headerName: String(t`Aspect Ratio`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.aspect_ratio,
      width: 125,
      maxWidth: 145,
    },
    {
      field: 'video_settings.background_color',
      headerName: String(t`BG Color`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.background_color,
      width: 110,
      maxWidth: 130,
    },
    {
      field: 'video_settings.transparent',
      headerName: String(t`Transparent`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.transparent,
      width: 115,
      maxWidth: 135,
    },
    {
      field: 'created_at',
      headerName: String(t`Created At`),
      filter: true,
      valueFormatter: (params: any) => params.value ? formatDateTime(params.value) : '',
      width: 180,
    },
    {
      field: 'actions',
      headerName: String(t`Actions`),
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      filter: false,
      width: 190,
      minWidth: 190,
      pinned: 'right',
    },
  ], [ActionsCellRenderer]);

  if (!selectedProject) return null;

  return (
    <div className="renders-page">
      <div className="renders-title-row">
        <h1>{t`Renders`} - {selectedProject.name}</h1>
        <RefreshCountdown nextRefreshAt={nextRefreshAt} refreshing={loading && renders.length > 0} />
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {loading && renders.length === 0 ? (
        <div aria-busy="true" aria-label={String(t`Loading renders...`)} role="status">
          <span className="sr-only">{t`Loading renders...`}</span>
          <SkeletonTable columns={8} rows={10} />
        </div>
      ) : !error && renders.length === 0 ? (
        <p>{t`No renders generated yet`}</p>
      ) : (
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          <AgGridReact
            rowData={renders}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
              sortable: false,
              filter: true,
            }}
            animateRows={false}
            rowSelection="single"
            theme="legacy"
          />
        </div>
      )}
    </div>
  );
};

export default Renders;
